import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  deleteUser,
  getAllUsers,
  updateUser,
  userBulkAction,
} from "../../../actionCreator/userManagementActionCreator";
import { useAlert } from "../../../context/AlertProvider";

export const useUserManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const sort = searchParams.get("sort");
  const [loading, setLoading] = useState();
  const users = useSelector((state) => state.UserManagementReducer.users);

  const onFilterChange = (key, val) => {
    if (val !== undefined && !["All Teams", "all", "All Roles"].includes(val)) {
      searchParams.set(key, val);
    } else {
      searchParams.delete(key);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleDelete = async (user) => {
    setLoading(true);
    const res = await dispatch(deleteUser(user?.id));
    if (res?.status === 204) {
      showAlert(
        "success",
        `${user?.firstName} ${user?.lastName} deleted successfully!`,
      );

      if (users?.content?.length === 1) {
        onFilterChange("page", page ? page - 1 : 1);
      } else {
        const allParams = {};
        for (const [key, value] of searchParams.entries()) {
          allParams[key] = value;
        }
        dispatch(
          getAllUsers({
            ...allParams,
            page: page
              ? users?.content?.length === 1
                ? page - 2
                : page - 1
              : 0,
          }),
        );
      }
    } else {
      showAlert("error", "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const handleActivateDeactivateUser = async (user) => {
    setLoading(true);

    const res = await dispatch(
      updateUser({ active: !user?.active, id: user?.id }),
    );
    if (res.status === 200) {
      showAlert(
        "success",
        `${user?.firstName} ${user?.lastName} ${user?.active ? "activated" : "deactivated"} successfully!`,
      );

      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }
      dispatch(
        getAllUsers({
          ...allParams,
          page: allParams?.page ? allParams?.page - 1 : 0,
        }),
      );
    } else {
      showAlert("error", "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const handleBulkAction = async (payload, callback) => {
    setLoading(true);
    const res = await dispatch(userBulkAction(payload));
    if (res?.status === 200) {
      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }
      if (payload?.action === "DEACTIVATE") {
        showAlert(
          "success",
          "The selected items have been successfully deactivated.",
        );

        dispatch(
          getAllUsers({
            ...allParams,
            page: page - 1,
          }),
        );
      } else if (payload?.action === "ACTIVATE") {
        showAlert(
          "success",
          "The selected items have been successfully activated.",
        );
        dispatch(
          getAllUsers({
            ...allParams,
            page: page - 1,
          }),
        );
      } else {
        showAlert(
          "success",
          "The selected items have been successfully deleted.",
        );
        dispatch(
          getAllUsers({
            ...allParams,
            page: users?.content?.length === 1 ? page - 2 : page - 1,
          }),
        );
        if (users?.content?.length === 1) {
          onFilterChange("page", page - 1);
        }
      }
      callback && callback();
    } else {
      showAlert("error", res.response?.data?.title);
    }
    setLoading(false);
  };

  useEffect(() => {
    const allParams = {};
    for (const [key, value] of searchParams.entries()) {
      allParams[key] = value;
    }
    dispatch(
      getAllUsers({
        ...allParams,
        page: allParams?.page ? allParams?.page - 1 : 0,
      }),
    );
  }, [dispatch, searchParams]);

  useEffect(() => onFilterChange("page", 1), [sort]);

  return {
    loading,
    onFilterChange,
    handleDelete,
    handleActivateDeactivateUser,
    handleBulkAction,
  };
};
